import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Checkbox,
  FormControlLabel,
  IconButton,
} from '@material-ui/core';
import { Delete as DeleteIcon, Edit as EditIcon, Add as AddIcon } from '@material-ui/icons';
import MaterialTable from 'material-table';
import { fetchAgenti, saveAgente, deleteAgente } from '../../../redux/actions/Agenti';
import Swal from 'sweetalert2';

const Agenti = () => {
  const dispatch = useDispatch();
  const { agenti, loading } = useSelector(state => state.agenti);
  const [open, setOpen] = useState(false);
  const [editingAgente, setEditingAgente] = useState(null);
  const [formData, setFormData] = useState({
    id: 0,
    nome: '',
    email: '',
    telefono: '',
    partita_iva: '',
    codice_fiscale: '',
    indirizzo: '',
    citta: '',
    cap: '',
    provincia: '',
    percentuale_standard: '',
    note: '',
    attivo: 1,
  });

  useEffect(() => {
    dispatch(fetchAgenti());
  }, [dispatch]);

  const handleOpen = (agente = null) => {
    if (agente) {
      setEditingAgente(agente);
      setFormData({
        id: agente.id,
        nome: agente.nome || '',
        email: agente.email || '',
        telefono: agente.telefono || '',
        partita_iva: agente.partita_iva || '',
        codice_fiscale: agente.codice_fiscale || '',
        indirizzo: agente.indirizzo || '',
        citta: agente.citta || '',
        cap: agente.cap || '',
        provincia: agente.provincia || '',
        percentuale_standard: agente.percentuale_standard || '',
        note: agente.note || '',
        attivo: agente.attivo,
      });
    } else {
      setEditingAgente(null);
      setFormData({
        id: 0,
        nome: '',
        email: '',
        telefono: '',
        partita_iva: '',
        codice_fiscale: '',
        indirizzo: '',
        citta: '',
        cap: '',
        provincia: '',
        percentuale_standard: '',
        note: '',
        attivo: 1,
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingAgente(null);
  };

  const handleChange = e => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value,
    }));
  };

  const handleSave = () => {
    if (!formData.nome.trim()) {
      Swal.fire('Errore', 'Il nome agente è obbligatorio', 'error');
      return;
    }

    dispatch(saveAgente(formData));
    handleClose();
    Swal.fire('Successo', 'Agente salvato con successo', 'success');
  };

  const handleDelete = id => {
    Swal.fire({
      title: 'Conferma eliminazione',
      text: 'Sei sicuro di voler eliminare questo agente?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sì, elimina',
      cancelButtonText: 'Annulla',
    }).then(result => {
      if (result.isConfirmed) {
        dispatch(deleteAgente(id));
        Swal.fire('Eliminato', 'Agente eliminato con successo', 'success');
      }
    });
  };

  const columns = [
    { title: 'Nome', field: 'nome' },
    { title: 'Email', field: 'email' },
    { title: 'Telefono', field: 'telefono' },
    { title: 'P.IVA', field: 'partita_iva' },
    {
      title: '% Standard',
      field: 'percentuale_standard',
      render: rowData => rowData.percentuale_standard ? `${rowData.percentuale_standard}%` : '-',
    },
    {
      title: 'Attivo',
      field: 'attivo',
      render: rowData => (rowData.attivo ? 'Sì' : 'No'),
    },
    {
      title: 'Azioni',
      render: rowData => (
        <Box>
          <IconButton size="small" onClick={() => handleOpen(rowData)}>
            <EditIcon />
          </IconButton>
          <IconButton size="small" onClick={() => handleDelete(rowData.id)}>
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box p={3}>
      <Box mb={2} display="flex" justifyContent="flex-end">
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => handleOpen()}>
          Nuovo Agente
        </Button>
      </Box>

      <MaterialTable
        title="Agenti"
        columns={columns}
        data={agenti}
        isLoading={loading}
        options={{
          search: true,
          sorting: true,
          pageSize: 10,
          pageSizeOptions: [10, 20, 50],
        }}
      />

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{editingAgente ? 'Modifica Agente' : 'Nuovo Agente'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Nome *"
              name="nome"
              value={formData.nome}
              onChange={handleChange}
              fullWidth
              required
            />
            <TextField label="Email" name="email" type="email" value={formData.email} onChange={handleChange} fullWidth />
            <TextField label="Telefono" name="telefono" value={formData.telefono} onChange={handleChange} fullWidth />
            <TextField label="Partita IVA" name="partita_iva" value={formData.partita_iva} onChange={handleChange} fullWidth />
            <TextField
              label="Codice Fiscale"
              name="codice_fiscale"
              value={formData.codice_fiscale}
              onChange={handleChange}
              fullWidth
            />
            <TextField label="Indirizzo" name="indirizzo" value={formData.indirizzo} onChange={handleChange} fullWidth />
            <Box display="flex" gap={2}>
              <TextField label="Città" name="citta" value={formData.citta} onChange={handleChange} fullWidth />
              <TextField label="CAP" name="cap" value={formData.cap} onChange={handleChange} />
              <TextField label="Provincia" name="provincia" value={formData.provincia} onChange={handleChange} />
            </Box>
            <TextField
              label="% Provvigione Standard"
              name="percentuale_standard"
              type="number"
              value={formData.percentuale_standard}
              onChange={handleChange}
              fullWidth
              inputProps={{ step: '0.01' }}
            />
            <TextField
              label="Note"
              name="note"
              value={formData.note}
              onChange={handleChange}
              fullWidth
              multiline
              rows={3}
            />
            <FormControlLabel
              control={<Checkbox checked={formData.attivo === 1} onChange={handleChange} name="attivo" />}
              label="Attivo"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Annulla</Button>
          <Button onClick={handleSave} color="primary" variant="contained">
            Salva
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Agenti;
